import React, {
    ReactElement,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiCopy, BiPencil, BiTrash } from "react-icons/bi";
import Confirm from "../../../../components/confirm/Confirm";
import EditableControl from "../../../../components/editable-text/EditableText";
import { createId, Json } from "./base";
import InputList from "./Input";
import OutputList from "./Output";
import Select from "../../../../components/select/Select";
import { NamedParameters } from "./Template";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import SortableList from "../../../../components/sortable-list/SortableList";

export default function WorkflowServiceActions({ json, onChange, rootJson }: {
    rootJson: Json;
    json: Json[];
    onChange: (current: Json[]) => void;
}): ReactElement {
    const [editing, setEditing] = useState<{groupIndex: number; actionIndex: number; action: Json}>();
    const handleChange = (index: number, value: Json) => {
        const copy = [...json];
        copy[index] = { ...copy[index], ...value };
        onChange(copy);
    };

    const handleChangeAction = (index: number, actionIndex: number, value: Json) => {
        const copy = [...json];
        if (!copy[index].actions) {
            copy[index].actions = [];
        }
        const current = (copy[index].actions as Json[])[actionIndex];
        (copy[index].actions as Json[])[actionIndex] = { ...current, ...value };

        onChange(copy);
    };

    const handleDeleteAction = (index: number, actionIndex: number) => {
        const copy = [...json];
        const group = copy[index];
        if (!group) {
            return;
        }

        (group.actions as Json[])?.splice(actionIndex, 1);
        onChange(copy);
    };

    const handleDuplicate = (index: number, actionIndex: number) => {
        const copy = [...json];
        const group = copy[index];
        if (!group) {
            return;
        }

        const actions = (group.actions as Json[]);
        if (!actions) {
            return;
        }
        const value = actions[actionIndex];
        actions.push(value);
        onChange(copy);
    };

    const editAction = (action: Json) => {
        setEditing({
            groupIndex: editing?.groupIndex as number,
            actionIndex: editing?.actionIndex as number,
            action: { ...editing?.action, ...action },
        });
    };

    const apis = (rootJson.apis ?? []) as Json[];
    const selectedApi = editing ? apis.find(api => api.id === (editing.action?.api as Json)?.apiId) : undefined;
    return (
        <div>
            {editing && (
                <Confirm
                    shouldShow
                    onHide={() => setEditing(undefined)}
                    size="lg"
                    confirmText="Save"
                    onConfirm={() => {
                        handleChangeAction(editing.groupIndex, editing.actionIndex, editing.action);
                        setEditing(undefined);
                    }}
                >
                    <div className="row">
                        <div className="col-12">
                            <ExpandingSection title="Request Info">
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Id</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Action Id"
                                        required
                                        onChange={evt => editAction({ id: evt.target.value })}
                                        value={editing.action?.id as string ?? ""}
                                    />
                                    <Form.Text>
                                        Id of the action
                                    </Form.Text>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-none btn-sm"
                                            onClick={() => editAction({
                                                id: `urn:fq:workflow-service-action:(urn:fq:workflow-service:`
                                                + `${rootJson.id},${createId()})`,
                                            })}
                                        >
                                            Generate Id
                                        </button>
                                    </div>
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bolder">Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Action Name"
                                        required
                                        onChange={evt => editAction({ name: evt.target.value })}
                                        value={editing.action.name as string ?? ""}
                                    />
                                    <Form.Text>
                                        Name of the action
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-5">
                                    <Form.Label className="fw-bolder">Description</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Action Description"
                                        required
                                        as="textarea"
                                        onChange={evt => editAction({ description: evt.target.value })}
                                        value={editing.action.description as string ?? ""}
                                    />
                                    <Form.Text>
                                        Description
                                    </Form.Text>
                                </Form.Group>
                            </ExpandingSection>
                            <ExpandingSection title="Input">

                                <InputList
                                    rootJson={rootJson}
                                    json={editing.action.input as Json[]}
                                    onChange={input => editAction({ input })}
                                />

                            </ExpandingSection>
                            <ExpandingSection title="Output">

                                <OutputList
                                    json={editing.action.output as Json[]}
                                    onChange={output => editAction({ output })}
                                    parentId={(editing.action as Json).id as string}
                                />
                            </ExpandingSection>
                            <ExpandingSection title="API">
                                <Select
                                    className="mb-2"
                                    onChange={apiId => editAction({
                                        api: {
                                            apiId: apiId.value,
                                            parameters: (editing.action?.api as Json)?.parameters,
                                        },
                                    })}
                                    value={!selectedApi ? undefined : {
                                        label: selectedApi.name as string,
                                        value: selectedApi.id as string,
                                    }}
                                    options={apis.map(api => ({
                                        label: api.name as string,
                                        value: api.id as string,
                                    }))}
                                />

                                <NamedParameters
                                    json={(editing.action?.api as Json)?.parameters as Json[] ?? []}
                                    onChange={parameters => editAction({
                                        api: {
                                            apiId: (editing.action?.api as Json)?.apiId,
                                            parameters,

                                        },
                                    })}

                                />
                            </ExpandingSection>
                        </div>
                    </div>
                </Confirm>
            )}

            {json?.map((group, groupIndex) => (
                <ExpandingSection
                    title={group.name as string ?? "New Section"}
                    topRightButton={<BiTrash className="text-light" />}
                    topRightClicked={() => {
                        const copy = [...json];
                        copy.splice(groupIndex, 1);
                        onChange(copy);
                    }}
                >
                    <h5>
                        <EditableControl value={group.name as string} onChange={name => handleChange(groupIndex, { name })} />
                    </h5>
                    <EditableControl
                        value={group.description as string}
                        placeholder="Description"
                        onChange={description => handleChange(groupIndex, { description })}
                    />

                    <div className=" mt-2">
                        <button
                            type="button"
                            className="btn btn-primary p-1 btn-sm"
                            onClick={() => setEditing({
                                groupIndex,
                                actionIndex: (group.actions as Json[])?.length ?? 0,
                                action: {},
                            })}
                        >
                            Add Action
                        </button>
                    </div>

                    <SortableList
                        getId={row => row.id as string}
                        items={group.actions as Json[] ?? []}
                        onReorder={(list: Json[]) => handleChange(groupIndex, { actions: list })}
                        listClassName="list-group list-group-flush mt-2"
                        itemClassName="list-group-item d-flex justify-content-between"
                        render={(action, actionIndex) => (
                            <>
                                <div>
                                    <strong>
                                        {action?.name as string}
                                    </strong>
                                    <div className="text-start">
                                        {action?.description as string}
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-none"
                                        onClick={() => setEditing({ groupIndex, actionIndex, action })}
                                    >
                                        <BiPencil className="text-light" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-none"
                                        onClick={() => handleDeleteAction(groupIndex, actionIndex)}
                                    >
                                        <BiTrash className="text-light" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-none"
                                        onClick={() => handleDuplicate(groupIndex, actionIndex)}
                                    >
                                        <BiCopy className="text-light" />
                                    </button>
                                </div>
                            </>
                        )}
                    />

                </ExpandingSection>

            ))}

            <button
                type="button"
                className="btn btn-primary"
                onClick={() => onChange([...(json ?? []), {}])}
            >
                Add Group
            </button>

        </div>
    );
}
